import type { ErrorCode } from '@repo/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
const BASE = '/api/v1';
const NO_REFRESH = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
];

let refreshing: Promise<boolean> | null = null;

/** One refresh at a time per tab; concurrent 401s wait for the same rotation. */
function refreshSession(): Promise<boolean> {
  refreshing ??= fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'fetch' },
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

async function send(
  method: Method,
  path: string,
  body?: unknown,
  extra: Record<string, string> = {},
): Promise<Response> {
  return fetch(`${BASE}${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'fetch',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...extra,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
}

async function toError(res: Response): Promise<ApiError> {
  try {
    const json = (await res.json()) as { error?: { code?: ErrorCode; message?: string; details?: unknown } };
    return new ApiError(
      res.status,
      json.error?.code ?? 'INTERNAL_ERROR',
      json.error?.message ?? res.statusText,
      json.error?.details,
    );
  } catch {
    return new ApiError(res.status, 'INTERNAL_ERROR', res.statusText);
  }
}

async function parse<T>(res: Response, retry: () => Promise<Response>, path: string): Promise<T> {
  let next = res;
  if (next.status === 401 && !NO_REFRESH.some((p) => path.startsWith(p))) {
    if (await refreshSession()) next = await retry();
  }
  if (!next.ok) throw await toError(next);
  return (await next.json()) as T;
}

/** JSON API call with cookie auth, CSRF header and transparent access-token refresh. */
export async function api<T>(
  path: string,
  options: { method?: Method; body?: unknown; headers?: Record<string, string> } = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const run = () => send(method, path, options.body, options.headers);
  return parse<T>(await run(), run, path);
}

/** Multipart upload (the browser sets the boundary). Same cookie and refresh behaviour as `api`. */
export async function apiForm<T>(path: string, body: FormData, method: Method = 'POST'): Promise<T> {
  const run = () =>
    fetch(`${BASE}${path}`, {
      method,
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'fetch' },
      body,
    });
  return parse<T>(await run(), run, path);
}

export const isApiError = (e: unknown, code?: ErrorCode): e is ApiError =>
  e instanceof ApiError && (!code || e.code === code);
